import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu, NavigationMenuContent, NavigationMenuItem,
  NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { VOLUNTEER } from "@/constants/testIds";
import NotificationsBell from "@/components/NotificationsBell";
import { api } from "@/lib/api";
import {
  LogOut, User, LayoutDashboard, Calendar, MessageSquare, Award, Compass,
  Users, ShieldCheck, Flame, Trophy, Menu, MapPinned, Building2, Search,
  HandHeart, Sprout, LifeBuoy, PartyPopper, Newspaper, MessageSquareQuote,
  Clock3, Settings, BarChart3, PlusCircle, Info,
} from "lucide-react";

// Phase 10 — intent-grouped navigation. Phase 17-B item 1 (2026-07): one word
// per concept — Give help has exactly THREE verbs (find / match / plan),
// orientation (/explore) moved to the logged-out surfaces, hosting moved
// under Community, Blog & News (/community) moved to the footer About cluster and
// Leaderboard lives inside /recognition (item 2).
const GIVE_HELP = [
  { to: "/opportunities", label: "Find opportunities", icon: Search, testId: "nav-give-opportunities" },
  { to: "/discover", label: "Quick match", icon: Flame, testId: "nav-give-discover" },
  { to: "/calendar", label: "Calendar", icon: Calendar, testId: "nav-give-calendar" },
];

const GET_HELP = [
  { to: "/help-request/new", label: "Ask a neighbour", icon: HandHeart, testId: "nav-get-ask" },
  { to: "/opportunities?type=help", label: "Browse help requests", icon: LifeBuoy, testId: "nav-get-browse" },
];

const COMMUNITY = [
  { to: "/collectives", label: "Collectives", icon: MapPinned, testId: "nav-community-collectives" },
  { to: "/opportunities?type=community", label: "Events & gatherings", icon: PartyPopper, testId: "nav-community-events" },
  { to: "/post-opportunity", label: "Host an event", icon: Sprout, testId: "nav-community-host" },
  { to: "/stories", label: "Stories", icon: MessageSquareQuote, testId: "nav-community-stories" },
  { to: "/teams", label: "Teams", icon: Users, testId: "nav-community-teams" },
];

const GROUPS = [
  { label: "Give help", testId: "nav-group-give-help", items: GIVE_HELP,
    activePaths: ["/opportunities", "/discover", "/calendar"] },
  { label: "Get help", testId: "nav-group-get-help", items: GET_HELP,
    activePaths: ["/help-request"] },
  { label: "Community", testId: "nav-group-community", items: COMMUNITY,
    activePaths: ["/collectives", "/post-opportunity", "/stories", "/teams"] },
];

const ORG_LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-org-dashboard" },
  { to: "/post-opportunity", label: "Post opportunity", icon: PlusCircle, testId: "nav-org-post" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-org-analytics" },
];

function MessagesIcon() {
  // Unread badge sums per-thread unread counts; refreshed every 60s.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => api.threads()
      .then((ts) => alive && setUnread(ts.reduce((s, t) => s + (t.unread || 0), 0)))
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return (
    <Link
      to="/messages"
      data-testid={VOLUNTEER.navMessages}
      aria-label={unread > 0 ? `Messages, ${unread} unread` : "Messages"}
      className="relative w-10 h-10 flex items-center justify-center rounded-full text-forest hover:bg-forest/5 transition-colors"
    >
      <MessageSquare className="w-5 h-5" />
      {unread > 0 && (
        <span data-testid="nav-messages-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-terracotta text-white text-[10px] font-semibold flex items-center justify-center px-1">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </Link>
  );
}

function DesktopGroups({ pathname }) {
  return (
    <NavigationMenu className="hidden lg:flex" data-testid="nav-groups">
      <NavigationMenuList>
        {GROUPS.map((g) => {
          const active = g.activePaths.some((p) => pathname === p || pathname.startsWith(p + "/"));
          return (
            <NavigationMenuItem key={g.label}>
              <NavigationMenuTrigger
                data-testid={g.testId}
                className={`h-9 bg-transparent hover:bg-terracotta hover:text-white focus:bg-terracotta focus:text-white data-[state=open]:bg-terracotta data-[state=open]:text-white data-[state=open]:hover:bg-terracotta data-[state=open]:focus:bg-terracotta rounded-full px-3.5 text-sm font-medium tracking-tight transition-colors ${active ? "text-forest" : "text-neutral-600"}`}
              >
                {active && <span className="w-1.5 h-1.5 rounded-full bg-terracotta group-data-[state=open]:bg-white mr-1.5 transition-colors" aria-hidden="true" />}
                {g.label}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="w-72 p-2" data-testid={`${g.testId}-menu`}>
                  {g.items.map((item) => {
                    const itemActive = pathname === item.to.split("?")[0] && !item.to.includes("?");
                    return (
                      <NavigationMenuLink asChild key={item.to}>
                        <Link
                          to={item.to}
                          data-testid={item.testId}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${itemActive ? "bg-forest/5 text-forest" : "text-neutral-700 hover:bg-forest/5 hover:text-forest"}`}
                        >
                          <item.icon className="w-[18px] h-[18px] text-forest/60 shrink-0" />
                          {item.label}
                        </Link>
                      </NavigationMenuLink>
                    );
                  })}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

function AvatarMenu({ user, logout }) {
  const nav = useNavigate();
  const isOrg = user.user_type === "organisation";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={VOLUNTEER.navProfileMenu} aria-label={`Account menu for ${user.name}`} className="hidden lg:flex items-center gap-2 hover:opacity-90">
          <Avatar className="w-9 h-9 border border-forest/10">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-forest text-white text-sm">{user.name?.[0]}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="font-medium">{user.name}</div>
          <div className="text-xs text-neutral-500 capitalize">{user.user_type || "Not onboarded"}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={VOLUNTEER.navDashboard} onClick={() => nav("/dashboard")}><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</DropdownMenuItem>
        <DropdownMenuItem data-testid="nav-my-profile" onClick={() => nav(isOrg ? `/o/${user.user_id}` : `/u/${user.user_id}`)}>
          <User className="w-4 h-4 mr-2" />{isOrg ? "My org page" : "My profile"}
        </DropdownMenuItem>
        {isOrg ? (
          <DropdownMenuItem data-testid="nav-org-calendar" onClick={() => nav("/calendar")}><Calendar className="w-4 h-4 mr-2" />Calendar</DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem data-testid="nav-log-hours" onClick={() => nav("/log-hours")}><Clock3 className="w-4 h-4 mr-2" />Log hours</DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-recognition" onClick={() => nav("/recognition")}><Award className="w-4 h-4 mr-2" />Recognition</DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem data-testid="nav-settings" onClick={() => nav("/onboarding")}><Settings className="w-4 h-4 mr-2" />Settings &amp; privacy</DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem data-testid="nav-admin" onClick={() => nav("/admin")}><ShieldCheck className="w-4 h-4 mr-2" />Admin</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={VOLUNTEER.navLogout} onClick={async () => { await logout(); nav("/"); }}>
          <LogOut className="w-4 h-4 mr-2" />Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileTabBar({ user }) {
  useEffect(() => {
    document.body.classList.toggle("has-tabbar", !!user);
    return () => document.body.classList.remove("has-tabbar");
  }, [user]);

  if (!user) return null;
  // Phase 17-B item 1: mobile tabs read Find / Quick match / Home / Messages.
  const tabs = [
    { to: "/opportunities", label: "Find", icon: Search, testId: "tab-find" },
    { to: "/discover", label: "Quick match", icon: Flame, testId: "tab-quick-match" },
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, testId: "tab-home" },
    { to: "/messages", label: "Messages", icon: MessageSquare, testId: "tab-messages" },
  ];
  return (
    <nav data-testid="mobile-tab-bar" className="lg:hidden fixed bottom-0 inset-x-0 z-40 civic-glass border-t border-white/40 pb-[env(safe-area-inset-bottom)]">
      <div className="grid grid-cols-4 h-16">
        {tabs.map(t => (
          <NavLink
            key={t.to}
            to={t.to}
            data-testid={t.testId}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${isActive ? "text-forest" : "text-neutral-500"}`}
          >
            {({ isActive }) => (
              <>
                <t.icon className={`w-5 h-5 ${isActive ? "text-terracotta" : ""}`} />
                {t.label}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function MobileItem({ item, go, prefix = "mobile-" }) {
  return (
    <button
      data-testid={`${prefix}${item.testId || item.label.toLowerCase()}`}
      onClick={() => go(item.to)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-700 hover:bg-forest/5 hover:text-forest transition-colors"
    >
      <item.icon className="w-[18px] h-[18px] text-forest/60" /> {item.label}
    </button>
  );
}

function MobileSectionLabel({ children }) {
  return <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-forest/60 font-bold">{children}</div>;
}

function MobileMenu({ user, logout }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const go = (to) => { setOpen(false); nav(to); };
  const isOrg = user?.user_type === "organisation";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-btn" aria-label="Open menu" className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full text-forest hover:bg-forest/5 transition-colors">
          <Menu className="w-6 h-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72 p-0 bg-cream" data-testid="mobile-menu-sheet">
        <div className="p-5 border-b border-forest/10">
          {user ? (
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 border border-forest/10">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="bg-forest text-white">{user.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="font-medium text-forest truncate">{user.name}</div>
                <div className="text-xs text-neutral-500 capitalize">{user.user_type || "Not onboarded"}</div>
              </div>
            </div>
          ) : (
            <div className="font-display text-lg text-forest">Civic<span className="text-terracotta">.</span>Hearth</div>
          )}
        </div>
        <nav className="p-3 pb-8 space-y-0.5 overflow-y-auto max-h-[calc(100vh-90px)]">
          {user && !isOrg && (
            <>
              <MobileSectionLabel>Give help</MobileSectionLabel>
              {GIVE_HELP.map((l) => <MobileItem key={l.to} item={l} go={go} />)}
              <MobileSectionLabel>Get help</MobileSectionLabel>
              {GET_HELP.map((l) => <MobileItem key={l.to} item={l} go={go} />)}
              <MobileSectionLabel>Community</MobileSectionLabel>
              {COMMUNITY.map((l) => <MobileItem key={l.to} item={l} go={go} />)}
            </>
          )}
          {user && isOrg && (
            <>
              {ORG_LINKS.map((l) => <MobileItem key={l.to} item={l} go={go} />)}
              <MobileItem item={{ to: "/messages", label: "Messages", icon: MessageSquare, testId: "nav-messages" }} go={go} />
              <MobileItem item={{ to: "/calendar", label: "Calendar", icon: Calendar, testId: "nav-calendar" }} go={go} />
            </>
          )}
          {!user && (
            <>
              <MobileItem item={{ to: "/explore", label: "Explore", icon: Compass, testId: "nav-explore" }} go={go} />
              <MobileItem item={{ to: "/partners", label: "For organisations", icon: Building2, testId: "nav-partners" }} go={go} />
              <MobileItem item={{ to: "/about", label: "About", icon: Info, testId: "nav-about-mobile" }} go={go} />
            </>
          )}
          {user && (
            <>
              <div className="h-px bg-forest/10 my-2" />
              <MobileSectionLabel>You</MobileSectionLabel>
              <MobileItem item={{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" }} go={go} />
              <MobileItem item={{ to: isOrg ? `/o/${user.user_id}` : `/u/${user.user_id}`, label: isOrg ? "My org page" : "My profile", icon: User, testId: "nav-profile" }} go={go} />
              {!isOrg && (
                <>
                  <MobileItem item={{ to: "/log-hours", label: "Log hours", icon: Clock3, testId: "nav-log-hours" }} go={go} />
                  <MobileItem item={{ to: "/recognition", label: "Recognition", icon: Award, testId: "nav-recognition" }} go={go} />
                </>
              )}
              <MobileItem item={{ to: "/onboarding", label: "Settings & privacy", icon: Settings, testId: "nav-settings" }} go={go} />
              {user.is_admin && (
                <MobileItem item={{ to: "/admin", label: "Admin", icon: ShieldCheck, testId: "nav-admin" }} go={go} />
              )}
              <button
                data-testid="mobile-nav-logout"
                onClick={async () => { setOpen(false); await logout(); nav("/"); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-[18px] h-[18px]" /> Sign out
              </button>
            </>
          )}
          {!user && (
            <div className="px-3 pt-3 space-y-2">
              <Button data-testid="mobile-nav-login" onClick={() => go("/login")} variant="outline" className="w-full rounded-full border-forest/30 text-forest hover:bg-forest/5">
                Sign in
              </Button>
              <Button data-testid="mobile-nav-get-started" onClick={() => go("/login")} className="w-full rounded-full bg-forest hover:bg-forest-hover">
                Get started
              </Button>
            </div>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isOrg = user?.user_type === "organisation";
  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-tight transition-colors ${isActive ? "text-forest" : "text-neutral-600 hover:text-forest"}`;

  return (
    <>
    <header className="sticky top-0 z-40 civic-glass border-b border-white/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo: dashboard when signed in, landing otherwise */}
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-logo" className="flex items-center gap-2 group shrink-0">
          <div className="w-8 h-8 rounded-md bg-forest flex items-center justify-center text-white font-display font-semibold">C</div>
          <span className="font-display text-lg tracking-tight text-forest">Civic<span className="text-terracotta">.</span>Hearth</span>
        </Link>

        {/* Desktop centre nav */}
        {user && !isOrg && <DesktopGroups pathname={pathname} />}
        {user && isOrg && (
          <nav className="hidden lg:flex items-center gap-5" data-testid="nav-org-bar">
            {ORG_LINKS.map(l => (
              <NavLink key={l.to} to={l.to} data-testid={l.testId} className={linkCls}>
                <span className="inline-flex items-center gap-1.5"><l.icon className="w-4 h-4" /> {l.label}</span>
              </NavLink>
            ))}
          </nav>
        )}
        {!user && (
          <nav className="hidden lg:flex items-center gap-6" data-testid="nav-logged-out">
            <NavLink to="/explore" data-testid="nav-explore" className={linkCls}>
              <span className="inline-flex items-center gap-1.5"><Compass className="w-4 h-4" /> Explore</span>
            </NavLink>
            <NavLink to="/partners" data-testid="nav-partners" className={linkCls}>
              <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4" /> For organisations</span>
            </NavLink>
            <NavLink to="/about" data-testid="nav-about" className={linkCls}>
              <span className="inline-flex items-center gap-1.5"><Info className="w-4 h-4" /> About</span>
            </NavLink>
          </nav>
        )}

        <div className="flex items-center gap-1.5 sm:gap-2">
          {!user ? (
            <>
              <button
                data-testid="nav-login-cta"
                onClick={() => nav("/login")}
                className="hidden sm:inline-flex text-sm font-medium text-forest hover:text-forest-hover px-3 py-2"
              >
                Sign in
              </button>
              <Button
                data-testid="nav-get-started"
                onClick={() => nav("/login")}
                className="hidden sm:inline-flex rounded-full bg-forest hover:bg-forest-hover text-white px-5"
              >
                Get started
              </Button>
            </>
          ) : (
            <>
              <MessagesIcon />
              <NotificationsBell />
              <AvatarMenu user={user} logout={logout} />
            </>
          )}
          <MobileMenu user={user} logout={logout} />
        </div>
      </div>
    </header>
    <MobileTabBar user={user} />
    </>
  );
}
